import { AlertCircle } from 'lucide-react';
import React, { useCallback } from 'react';
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "../../../../components/ui/card";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { Skeleton } from "../../../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { getErrorMessage } from '../utils/helpers';
import NodeItem from './NodeItem';
import PodHealth from './PodHealth';
import { NodeListProps } from './types';

const NodesList: React.FC<NodeListProps> = ({
  nodePodsMap,
  nodeMetrics,
  podMetrics,
  podSessionMap,
  isLoading,
  isLoadingSessions,
  error,
  expandedNodes,
  showGatewayNodes,
  showNormalNodes,
  toggleNode,
  setShowGatewayNodes,
  setShowNormalNodes
}) => {
  // Calculate session count for each node for sorting
  const getNodeSessionCount = useCallback((nodeName: string, nodeData: any) => {
    if (isLoadingSessions) return 0;
    
    let sessionCount = 0;
    nodeData.pods.forEach((pod: any) => {
      const sessions = podSessionMap.get(pod.metadata.name) || [];
      if (sessions.length > 0) {
        sessionCount++;
      }
    });
    return sessionCount;
  }, [isLoadingSessions, podSessionMap]);

  // Sort nodes by session count (lowest to highest)
  const sortedNodes = React.useMemo(() => {
    return Array.from(nodePodsMap.entries())
      .map(([nodeName, nodeData]) => ({
        nodeName,
        nodeData,
        sessionCount: getNodeSessionCount(nodeName, nodeData)
      }))
      .sort((a, b) => a.sessionCount - b.sessionCount);
  }, [nodePodsMap, getNodeSessionCount]);

  // Get all pods from the nodePodsMap for the Pod Health tab
  const allPods = React.useMemo(() => {
    const pods: any[] = [];
    
    // Only include pods from visible node types
    Array.from(nodePodsMap.entries()).forEach(([_, nodeData]) => {
      if ((nodeData.nodeType === 'gateway' && showGatewayNodes) ||
          (nodeData.nodeType === 'normal' && showNormalNodes)) {
        pods.push(...nodeData.pods);
      }
    });
    
    return pods;
  }, [nodePodsMap, showGatewayNodes, showNormalNodes]);

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Cluster Resources</CardTitle>
          <CardDescription>
            Monitor nodes, pods, and resource usage across the cluster
          </CardDescription>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="show-gateway" 
              checked={showGatewayNodes}
              onCheckedChange={(checked) => setShowGatewayNodes(checked as boolean)} 
            />
            <Label htmlFor="show-gateway" className="text-sm font-medium">
              Gateway Nodes
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="show-normal" 
              checked={showNormalNodes}
              onCheckedChange={(checked) => setShowNormalNodes(checked as boolean)} 
            />
            <Label htmlFor="show-normal" className="text-sm font-medium">
              Normal Nodes
            </Label>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {getErrorMessage(error) || 'Failed to load cluster data. Please try again later.'}
            </AlertDescription>
          </Alert>
        ) : (
          <Tabs defaultValue="node-utilization">
            <TabsList className="mb-4">
              <TabsTrigger value="node-utilization">Node Utilization</TabsTrigger>
              <TabsTrigger value="pod-health">Pod Health</TabsTrigger>
            </TabsList>
            
            <TabsContent value="node-utilization" className="space-y-4">
              {nodePodsMap.size === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  {!showGatewayNodes && !showNormalNodes 
                    ? 'No nodes selected. Please select at least one node type.'
                    : 'No nodes found in the cluster.'}
                </div>
              ) : (
                sortedNodes.map(({ nodeName, nodeData }) => (
                  <NodeItem
                    key={nodeName}
                    nodeName={nodeName}
                    nodeData={nodeData}
                    nodeMetrics={nodeMetrics}
                    podMetrics={podMetrics}
                    podSessionMap={podSessionMap}
                    isLoadingSessions={isLoadingSessions ?? false}
                    isExpanded={expandedNodes.has(nodeName)}
                    onToggleExpand={toggleNode}
                  />
                ))
              )}
            </TabsContent>
            
            <TabsContent value="pod-health">
              <PodHealth 
                pods={allPods}
                podMetrics={podMetrics}
                isLoadingMetrics={!!isLoading}
                showGatewayNodes={showGatewayNodes}
                showNormalNodes={showNormalNodes}
              />
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default NodesList; 